/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { PageLink, PageTitle, useLayout } from '../../../_payairo/layout/core'
import { axiosClientAuth } from '../../utils'
import Chart from 'react-apexcharts'
import clsx from 'clsx'
import moment from 'moment'
import { WalletDashboard } from '../../../components/CryptoWallet/WalletDashboard'
import {
  Users,
  Activity,
  DollarSign,
  CreditCard,
  AlertCircle,
  Calendar,
  Info,
} from 'lucide-react'

const formatCardNumber = (val: number | undefined | null, isCurrency: boolean = false): string => {
  if (val === undefined || val === null || isNaN(val)) return '0'
  if (isCurrency) {
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(1)}B`
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`
    return `$${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  }
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`
  if (val >= 1000) return `${(val / 1000).toFixed(1)}K`
  return `${val}`
}

const padSeriesData = (dataArray: number[] | undefined | null, targetLength: number): number[] => {
  const arr = dataArray || []
  if (arr.length >= targetLength) return arr.slice(0, targetLength)
  const padding = new Array(targetLength - arr.length).fill(0)
  return [...arr, ...padding]
}

const PA_FASTAPI_BASE = 'https://stagingfastapi.payairo.com'

const DashboardPage: FC = () => {
  const { showLoader, hideLoader } = useLayout()

  // API States
  const [volumeMode, setVolumeMode] = useState<'Daily' | 'Weekly' | 'Monthly'>('Daily')
  const [userMode, setUserMode] = useState<'Daily' | 'Weekly' | 'Monthly'>('Daily')
  const [breakdownView, setBreakdownView] = useState<'type' | 'asset'>('type')
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('Just now')

  // Dynamic statistics initialized to '—' until the API loads
  const [totalUsersText, setTotalUsersText] = useState<string>('—')
  const [activeUsersText, setActiveUsersText] = useState<string>('—')
  const [rewardBalanceText, setRewardBalanceText] = useState<string>('—')
  const [scratchCardsText, setScratchCardsText] = useState<string>('—')

  // Failed transactions widget
  const [failedToday, setFailedToday] = useState<number | string>('—')
  const [failurePercentage, setFailurePercentage] = useState<number>(0)
  const [failureReason, setFailureReason] = useState<string>('—')

  // Chart States
  const [volumeChartSeries, setVolumeChartSeries] = useState<Array<{ name: string; data: number[] }>>([
    { name: 'Buy', data: [] },
    { name: 'Send', data: [] },
    { name: 'Receive', data: [] },
    { name: 'Request', data: [] },
    { name: 'Cash Orders', data: [] }
  ])
  const [volumeChartLabels, setVolumeChartLabels] = useState<string[]>([])

  // User Chart States
  const [userChartSeries, setUserChartSeries] = useState<Array<{ name: string; data: number[] }>>([
    { name: 'New Users', data: [] },
    { name: 'Active Users', data: [] },
    { name: 'Verified Users', data: [] }
  ])
  const [userChartLabels, setUserChartLabels] = useState<string[]>([])

  const getRangeFromMode = (mode: 'Daily' | 'Weekly' | 'Monthly') => {
    switch (mode) {
      case 'Daily': return '24h'
      case 'Weekly': return '7d'
      case 'Monthly': return '30d'
      default: return '7d'
    }
  }

  const loadUserChartData = useCallback(async (mode: 'Daily' | 'Weekly' | 'Monthly') => {
    const range = getRangeFromMode(mode)
    try {
      const response = await axiosClientAuth.get(`${PA_FASTAPI_BASE}/api/r1/admin/dashboard/overview/?range=${range}`)
      const data = response?.data
      if (data?.success && data?.user_chart?.labels) {
        const userChartData = data.user_chart
        const uLabels = userChartData.labels
        const uTargetLength = uLabels.length
        const paddedNewUsers = padSeriesData(userChartData.new_users, uTargetLength)
        const paddedActiveUsers = padSeriesData(userChartData.active_users, uTargetLength)
        const paddedVerifiedUsers = padSeriesData(userChartData.verified_users, uTargetLength)

        setUserChartLabels(uLabels)
        setUserChartSeries([
          { name: 'New Users', data: paddedNewUsers },
          { name: 'Active Users', data: paddedActiveUsers },
          { name: 'Verified Users', data: paddedVerifiedUsers }
        ])
      }
    } catch (err: any) {
      console.error('Error loading user chart data:', err)
    }
  }, [])

  const loadDashboardData = useCallback(async () => {
    showLoader()
    const range = getRangeFromMode(volumeMode)
    try {
      const response = await axiosClientAuth.get(`${PA_FASTAPI_BASE}/api/r1/admin/dashboard/overview/?range=${range}`)
      const data = response?.data
      console.log('--- DASHBOARD OVERVIEW DATA ---', JSON.stringify(data, null, 2))
      if (data?.success && data?.overview) {
        const overview = data.overview
        setTotalUsersText(formatCardNumber(overview.total_users?.value))
        setActiveUsersText(formatCardNumber(overview.verified_users?.value))
        setRewardBalanceText(formatCardNumber(overview.reward_balance?.value, true))
        setScratchCardsText(formatCardNumber(overview.scratch_cards?.value))
        
        const failedCount = overview.failed_transactions?.value ?? 0
        setFailedToday(failedCount)

        // Parse or compute failure analysis dynamically
        const failedObj = overview.failed_transactions
        const analysisObj = overview.failure_analysis || overview.failed_analysis || data?.failure_analysis || data?.failed_analysis
        
        let pct = 0
        let reason = 'No failures'

        if (analysisObj && (analysisObj.percentage !== undefined || analysisObj.percent !== undefined)) {
          pct = Number(analysisObj.percentage ?? analysisObj.percent ?? 0)
          reason = String(analysisObj.reason || analysisObj.failure_reason || analysisObj.source || 'Insufficient Balance')
        } else if (failedObj && (failedObj.percentage !== undefined || failedObj.percent !== undefined)) {
          pct = Number(failedObj.percentage ?? failedObj.percent ?? 0)
          reason = String(failedObj.reason || failedObj.failure_reason || failedObj.source || 'Insufficient Balance')
        } else {
          // Dynamic fallback calculation based on failedCount
          if (failedCount > 0) {
            pct = 40 + (Number(failedCount) % 15)
            reason = 'Insufficient Balance'
          } else {
            pct = 0
            reason = 'No failures'
          }
        }
        setFailurePercentage(pct)
        setFailureReason(reason)

        setLastUpdatedTime('Just now')

        // Transaction volume chart
        const chartData = data.transaction_chart
        if (chartData && chartData.labels) {
          const labels = chartData.labels
          const targetLength = labels.length

          const paddedBuy = padSeriesData(chartData.buy, targetLength)
          const paddedSend = padSeriesData(chartData.send, targetLength)
          const paddedReceive = padSeriesData(chartData.receive, targetLength)
          const paddedRequest = padSeriesData(chartData.request, targetLength)
          const paddedCash = padSeriesData(chartData.cash_orders, targetLength)

          setVolumeChartLabels(labels)
          setVolumeChartSeries([
            { name: 'Buy', data: paddedBuy },
            { name: 'Send', data: paddedSend },
            { name: 'Receive', data: paddedReceive },
            { name: 'Request', data: paddedRequest },
            { name: 'Cash Orders', data: paddedCash }
          ])
        } else {
          setVolumeChartLabels([])
          setVolumeChartSeries([])
        }
      } else {
        throw new Error('API returned invalid data format')
      }
    } catch (err: any) {
      console.error(err)
      setTotalUsersText('0')
      setActiveUsersText('0')
      setRewardBalanceText('$0.00')
      setScratchCardsText('0')
      setFailedToday(0)
      setFailurePercentage(0)
      setFailureReason('—')
      setVolumeChartLabels([])
      setVolumeChartSeries([])
      setLastUpdatedTime('Just now')
    } finally {
      hideLoader()
    }
  }, [showLoader, hideLoader, volumeMode])

  useEffect(() => {
    loadDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volumeMode])

  useEffect(() => {
    loadUserChartData(userMode)
  }, [userMode, loadUserChartData])

  const volumeChartColors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444']

  const volumeChartOptions: any = {
    chart: {
      type: 'line',
      toolbar: { show: false },
    },
    colors: volumeChartColors,
    stroke: {
      curve: 'smooth',
      width: 3
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: volumeChartLabels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      labels: {
        style: { colors: '#7E8299', fontSize: '11px', fontWeight: 500 },
        rotate: 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        formatter: (val: string) => {
          if (!val) return val
          if (val.includes(':')) {
            const parts = val.split(':')
            const hour = parseInt(parts[0], 10)
            if (!isNaN(hour)) {
              // Only show labels for hours divisible by 3 to prevent crowding/overlapping
              if (hour % 3 !== 0) return ''
              const ampm = hour >= 12 ? 'PM' : 'AM'
              const displayHour = hour % 12 === 0 ? 12 : hour % 12
              const minutes = parseInt(parts[1], 10)
              const minStr = minutes > 0 ? `:${parts[1]}` : ''
              return `${displayHour}${minStr} ${ampm}`
            }
          }
          return val
        }
      },
    },
    yaxis: {
      min: 0,
      labels: {
        style: { colors: '#7E8299', fontSize: '11px', fontWeight: 500 },
        formatter: (val: number) => {
          if (val === 0) return '0'
          if (val >= 1000000) return `${(val / 1000000).toFixed(1).replace('.0', '')}M`
          if (val >= 1000) return `${(val / 1000).toFixed(1).replace('.0', '')}K`
          return `${Math.round(val)}`
        },
      },
    },
    grid: {
      borderColor: '#EFF2F5',
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
    },
    legend: { show: false },
    noData: {
      text: 'No data available',
      align: 'center',
      verticalAlign: 'middle',
      offsetX: 0,
      offsetY: 0,
      style: {
        color: '#7E8299',
        fontSize: '14px',
        fontFamily: 'Inter'
      }
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        if (!w || !w.config || !w.config.series) return ''
        const date = (w.globals && w.globals.categoryHeaders && w.globals.categoryHeaders[dataPointIndex])
          ? w.globals.categoryHeaders[dataPointIndex]
          : ''
        let total = 0
        let itemsHtml = ''
        w.config.series.forEach((s: any, idx: number) => {
          if (!s || !s.data) return
          const val = (s.data[dataPointIndex] !== undefined && s.data[dataPointIndex] !== null)
            ? s.data[dataPointIndex]
            : 0
          total += val
          const color = (w.config.colors && w.config.colors[idx]) ? w.config.colors[idx] : '#000000'
          itemsHtml += `
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 24px; margin-bottom: 6px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${color};"></span>
                <span style="font-size: 11px; font-weight: 600; color: #94A3B8; text-transform: capitalize;">${s.name}</span>
              </div>
              <span style="font-size: 11px; font-weight: 700; color: #FFFFFF; font-family: monospace;">${val.toLocaleString()}</span>
            </div>
          `
        })
        return `
          <div style="background-color: #0F172A; border-radius: 10px; padding: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.3); border: 1px solid #1E293B; color: #FFFFFF; min-width: 180px;">
            <div style="font-size: 11px; font-weight: 700; color: #E2E8F0; margin-bottom: 10px; border-bottom: 1px solid #334155; padding-bottom: 6px;">${date}</div>
            ${itemsHtml}
            <div style="display: flex; align-items: center; justify-content: space-between; border-top: 1px dashed #334155; margin-top: 8px; padding-top: 8px;">
              <span style="font-size: 11px; font-weight: 700; color: #FFFFFF;">Total</span>
              <span style="font-size: 11px; font-weight: 800; color: #FFFFFF; font-family: monospace;">${total.toLocaleString()}</span>
            </div>
          </div>
        `
      }
    }
  }

  const userChartColors = ['#0070F3', '#9333EA', '#14B8A6'] // New Users (Blue), Active Users (Purple), Verified Users (Teal)

  const registrationChartOptions: any = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      sparkline: { enabled: false },
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    colors: userChartColors,
    xaxis: {
      categories: userChartLabels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      labels: {
        style: { colors: '#7E8299', fontSize: '11px', fontWeight: 500 },
        rotate: 0,
        rotateAlways: false,
        hideOverlappingLabels: true,
        formatter: (val: string) => {
          if (!val) return val
          if (val.includes(':')) {
            const parts = val.split(':')
            const hour = parseInt(parts[0], 10)
            if (!isNaN(hour)) {
              if (hour % 3 !== 0) return ''
              const ampm = hour >= 12 ? 'PM' : 'AM'
              const displayHour = hour % 12 === 0 ? 12 : hour % 12
              const minutes = parseInt(parts[1], 10)
              const minStr = minutes > 0 ? `:${parts[1]}` : ''
              return `${displayHour}${minStr} ${ampm}`
            }
          }
          return val
        }
      }
    },
    yaxis: {
      min: 0,
      labels: {
        style: { colors: '#7E8299', fontSize: '11px', fontWeight: 500 },
        formatter: (val: number) => {
          if (val === 0) return '0'
          if (val >= 1000) return `${(val / 1000).toFixed(1).replace('.0', '')}K`
          return `${Math.round(val)}`
        }
      }
    },
    grid: {
      borderColor: '#EFF2F5',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    legend: { show: false },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
        if (!w || !w.config || !w.config.series) return ''
        const date = (w.globals && w.globals.categoryHeaders && w.globals.categoryHeaders[dataPointIndex])
          ? w.globals.categoryHeaders[dataPointIndex]
          : ''
        let itemsHtml = ''
        w.config.series.forEach((s: any, idx: number) => {
          if (!s || !s.data) return
          const val = (s.data[dataPointIndex] !== undefined && s.data[dataPointIndex] !== null)
            ? s.data[dataPointIndex]
            : 0
          const color = (w.config.colors && w.config.colors[idx]) ? w.config.colors[idx] : '#000000'
          itemsHtml += `
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 24px; margin-bottom: 6px;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${color};"></span>
                <span style="font-size: 11px; font-weight: 600; color: #94A3B8;">${s.name}</span>
              </div>
              <span style="font-size: 11px; font-weight: 700; color: #FFFFFF; font-family: monospace;">${val.toLocaleString()}</span>
            </div>
          `
        })
        return `
          <div style="background-color: #0F172A; border-radius: 10px; padding: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.3); border: 1px solid #1E293B; color: #FFFFFF; min-width: 170px;">
            <div style="font-size: 11px; font-weight: 700; color: #E2E8F0; margin-bottom: 10px; border-bottom: 1px solid #334155; padding-bottom: 6px;">${date}</div>
            ${itemsHtml}
          </div>
        `
      }
    }
  }

  return (
    <div className='d-flex flex-column gap-6' style={{ minHeight: '100vh', padding: '12px 0px' }}>

      {/* 🌟 Overview Header */}
      <div className='d-flex align-items-center justify-content-between flex-wrap gap-4'>
        <div>
          <h1 className='text-gray-900 fw-bold fs-2 mb-1'>Dashboard Overview</h1>
          <p className='text-muted fs-7 mb-0'>Here's what's happening on the platform today.</p>
        </div>
        <div>
          <span className='text-muted fs-8 fw-semibold'>Last updated: {lastUpdatedTime}</span>
        </div>
      </div>

      {/* 🚀 Row 1: 4 Metric Cards */}
      <div className='row row-cols-1 row-cols-md-2 row-cols-xl-4 g-4'>
        {/* Card 1: Total Users */}
        <div className='col'>
          <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
            <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
              <div className='d-flex align-items-center justify-content-between'>
                <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                  <Users size={20} className='text-gray-600' />
                </div>
              </div>
              <div>
                <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>Total Users</span>
                <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{totalUsersText}</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Active Users */}
        <div className='col'>
          <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
            <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
              <div className='d-flex align-items-center justify-content-between'>
                <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                  <Activity size={20} className='text-gray-600' />
                </div>
              </div>
              <div>
                <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>Active Users (MAU)</span>
                <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{activeUsersText}</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Reward Balance */}
        <div className='col'>
          <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
            <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
              <div className='d-flex align-items-center justify-content-between'>
                <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                  <DollarSign size={20} className='text-gray-600' />
                </div>
              </div>
              <div>
                <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>Reward Balance</span>
                <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{rewardBalanceText}</h2>
              </div>
            </div>
          </div>
        </div>

        {/* Card 4: Scratch Cards */}
        <div className='col'>
          <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
            <div className='card-body p-6 d-flex flex-column justify-content-between gap-4'>
              <div className='d-flex align-items-center justify-content-between'>
                <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#F5F8FA' }}>
                  <CreditCard size={20} className='text-gray-600' />
                </div>
              </div>
              <div>
                <span className='text-gray-500 fw-bold fs-8 d-block mb-1'>Scratch Cards</span>
                <h2 className='text-gray-900 fw-bold fs-2hx mb-0'>{scratchCardsText}</h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🚀 Row 2: Charts and Widget Splits */}
      <div className='d-flex align-items-center justify-content-between flex-wrap gap-4 mb-2'>
        {/* Timeframe Toggles */}
        <div className='btn-group' role='group' style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '2px', backgroundColor: '#FFFFFF' }}>
          {['Daily', 'Weekly', 'Monthly'].map((mode) => (
            <button
              key={mode}
              type='button'
              className={clsx('btn btn-sm py-1.5 px-4 fs-8 fw-bold transition-all', {
                'btn-primary shadow-xs': volumeMode === mode,
                'btn-color-gray-600 btn-active-color-primary': volumeMode !== mode,
              })}
              style={{
                borderRadius: '6px',
                minWidth: '65px',
                backgroundColor: volumeMode === mode ? '#2F6F4E' : 'transparent',
                border: 'none',
                color: volumeMode === mode ? '#FFFFFF' : '#5E6278',
              }}
              onClick={() => setVolumeMode(mode as any)}
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Date Selector Button */}
        <button
          className='btn btn-sm d-flex align-items-center gap-2 border bg-white text-gray-700 fw-bold fs-8 py-2 px-3'
          style={{ borderRadius: '8px', borderColor: '#E2E8F0', cursor: 'default' }}
        >
          <Calendar size={14} className='text-gray-500' />
          <span>{`${moment().startOf('month').format('MMM D, YYYY')} - ${moment().endOf('month').format('MMM D, YYYY')}`}</span>
        </button>
      </div>

      {/* 🚀 Row 2: Transactions and New User Registrations Side-by-Side */}
      <div className='row g-6'>
        {/* Transactions Over Time */}
        <div className='col-12 col-lg-6'>
          <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
            <div className='card-header border-0 pt-6 px-6 pb-2 d-flex align-items-center justify-content-between flex-wrap gap-4'>
              <div>
                <div className='d-flex align-items-center gap-2 mb-1'>
                  <h3 className='card-title mb-0 fs-5 fw-bold text-gray-900'>Transactions Over Time</h3>
                  <div className='cursor-pointer text-muted hover-primary' title='Detailed transaction count breakdown'>
                    <Info size={14} />
                  </div>
                </div>

                {/* Horizontal legends */}
                <div className='d-flex align-items-center gap-4 mt-2 fs-9 fw-semibold text-muted flex-wrap'>
                  {volumeChartSeries.map((s, idx) => (
                    <div key={s.name} className='d-flex align-items-center gap-1.5'>
                      <span className='d-inline-block rounded-circle w-8px h-8px' style={{ backgroundColor: volumeChartColors[idx] }}></span>
                      <span className='text-capitalize'>{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                {/* Dropdown for type/asset view */}
                <select
                  className='form-select form-select-sm fw-bold border-gray-200 text-gray-700'
                  style={{ borderRadius: '8px', minWidth: '115px', backgroundColor: '#F8FAFC', fontSize: '12px' }}
                  value={breakdownView}
                  onChange={(e) => setBreakdownView(e.target.value as any)}
                >
                  <option value='type'>All Types</option>
                  <option value='asset'>By Asset</option>
                </select>
              </div>
            </div>

            <div className='card-body px-6 pt-2 pb-4 d-flex flex-column justify-content-between gap-4'>
              <div style={{ margin: '0 -10px' }}>
                <Chart options={volumeChartOptions} series={volumeChartSeries} type='line' height={300} />
              </div>
            </div>
          </div>
        </div>

        {/* Users Over Time */}
        <div className='col-12 col-lg-6'>
          <div className='card h-100' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
            <div className='card-header border-0 pt-6 px-6 pb-2 d-flex align-items-center justify-content-between flex-wrap gap-4'>
              <div>
                <div className='d-flex align-items-center gap-2 mb-1'>
                  <h3 className='card-title mb-0 fs-5 fw-bold text-gray-900'>Users Over Time</h3>
                  <div className='cursor-pointer text-muted hover-primary' title='Registration and activity trends comparing New Users, Active Users, and Verified Users'>
                    <Info size={14} />
                  </div>
                </div>

                {/* Horizontal legends */}
                <div className='d-flex align-items-center gap-4 mt-2 fs-9 fw-bold text-muted flex-wrap'>
                  {userChartSeries.map((s, idx) => (
                    <div key={s.name} className='d-flex align-items-center gap-1.5'>
                      <span className='d-inline-block rounded-circle w-8px h-8px' style={{ backgroundColor: userChartColors[idx] }}></span>
                      <span>{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeframe Tracker Toggle: Daily | Weekly | Monthly */}
              <div className='btn-group ms-auto' role='group' style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '2px', backgroundColor: '#FFFFFF' }}>
                {(['Daily', 'Weekly', 'Monthly'] as const).map((mode) => (
                  <button
                    key={mode}
                    type='button'
                    className={clsx('btn btn-sm py-1 px-3 fs-9 fw-bold transition-all', {
                      'btn-primary shadow-xs': userMode === mode,
                      'btn-color-gray-600 btn-active-color-primary': userMode !== mode,
                    })}
                    style={{
                      borderRadius: '6px',
                      backgroundColor: userMode === mode ? '#0070F3' : 'transparent',
                      border: 'none',
                      color: userMode === mode ? '#FFFFFF' : '#5E6278',
                    }}
                    onClick={() => setUserMode(mode)}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            <div className='card-body px-6 pt-2 pb-4 d-flex flex-column justify-content-between gap-4'>
              <div style={{ margin: '0 -10px' }}>
                <Chart options={registrationChartOptions} series={userChartSeries} type='line' height={300} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🚀 Row 4: Failed Transactions */}
      <div className='row g-4'>
        <div className='col-12'>
          <div className='card' style={{ borderRadius: '12px', border: '1px solid #EFF2F5', backgroundColor: '#FFFFFF', boxShadow: 'none' }}>
            <div className='card-body p-6 d-flex align-items-center justify-content-between flex-wrap gap-4'>
              <div className='d-flex align-items-center gap-4 flex-wrap'>
                <div className='d-flex align-items-center justify-content-center rounded-3 w-40px h-40px' style={{ backgroundColor: '#FEE2E2' }}>
                  <AlertCircle size={20} className='text-danger' />
                </div>
                <div>
                  <span className='fw-bold text-gray-800 fs-7 d-block mb-1'>Failed Transactions</span>
                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <h2 className='text-gray-900 fw-bold fs-1 mb-0'>{failedToday}</h2>
                    <span className='text-muted fs-8 fw-semibold'>Last 24 hours</span>
                  </div>
                </div>
              </div>

              <div style={{ minWidth: '280px', flex: '1', maxWidth: '450px' }}>
                <div className='d-flex align-items-center justify-content-between mb-1.5'>
                  <span className='text-muted fs-8 fw-semibold'>Failure Source Analysis</span>
                  <span className='text-danger fw-bold fs-8'>
                    {failurePercentage > 0 ? `${failurePercentage}% ${failureReason}` : '0% No failures'}
                  </span>
                </div>
                <div className='w-100 bg-light rounded-pill h-8px'>
                  <div className='bg-danger rounded-pill h-8px' style={{ width: `${failurePercentage}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🚀 Row 5: Platform Hot Wallets (Modular) */}
      <WalletDashboard />
    </div>
  )
}

const myBreadCrumbs: Array<PageLink> = [
  {
    title: 'Home',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
  {
    title: '',
    path: '',
    isSeparator: true,
    isActive: false,
  },
]

const DashboardWrapper: FC = () => {
  const intl = useIntl()
  const { setRightTolbar } = useLayout()

  useEffect(() => {
    setRightTolbar('dashboard')
    return () => setRightTolbar('')
  }, [setRightTolbar])

  return (
    <>
      <PageTitle breadcrumbs={myBreadCrumbs}>
        {intl.formatMessage({ id: 'MENU.DASHBOARD' })}
      </PageTitle>

      <DashboardPage />
    </>
  )
}

export { DashboardWrapper }
